import {useContext, useState} from 'react';

import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {Link as RouterLink, useNavigate, useSearchParams} from 'react-router-dom';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {Alert, Box, Grid, Stack, Typography} from '@mui/material';

import {AssessmentResults} from '../applicants/components/DetailedCardView/MatchInformation/AssessmentResults';
import {RejectionInfoBox} from '../applicants/components/NewMatchDetails/RejectionInfoBox';

import {TalentPoolCriterionAnalysisRow} from './components/TalentPoolCriterionAnalysisRow';
import {MatchStatusLabel} from './MatchStatusLabel';
import {TalentPoolApplicationMatchDialog} from './TalentPoolApplicationMatchDialog';
import {
  type CreateTalentPoolMatchResponse,
  type JobMatchCriterionDetail,
  type TalentPoolProfileApplication,
} from './types/TalentPoolProfileTypes';
import {TALENT_POOL_STATUS_IDS} from './types/TalentPoolTypes';
import {normalizeAssessmentQuestions, parseCriterionDetail} from './utils/talentPoolApplicationCardUtils';
import {EmpionButton} from '../../../../components/EmpionButton/EmpionButton';
import {EmpionCollapsibleSection} from '../../../../components/EmpionCollapsibleSection/EmpionCollapsibleSection';
import {EmpionMatchScoresGrid} from '../../../../components/EmpionMatchScoresGrid/EmpionMatchScoresGrid';
import {CompanyContext} from '../../../../contexts/CompanyContext/CompanyContext';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {TALENT_POOL_SUMMARY_QUERY_KEY} from '../../../../hooks/useTalentPoolSummary';
import {greenColors, orangeColors} from '../../../../layouts/theme/designSystem';
import {Post} from '../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../lib/api/POST/PostRoutes';
import {MATCH_DETAILS_FROM_TALENT_POOL, MATCH_DETAILS_RETURN_TO_PARAM} from '../../../../lib/matchDetailsNav';
import {transformDate} from '../../../../utils/dateUtils/dateFormattingUtils';

type Props = {
  application: TalentPoolProfileApplication;
};

type QualificationSectionId = 'must' | 'can';

const cardShellSx = {
  mb: 3,
  p: 2,
  borderRadius: '16px',
  bgcolor: 'white',
  border: `1px solid ${orangeColors[3]}`,
  boxShadow: '0 1px 4px rgba(9, 44, 76, 0.06), 0 2px 12px rgba(9, 44, 76, 0.04)',
} as const;

export const TalentPoolApplicationCard = ({application}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'talentPoolProfilePage.applicationCard'});
  const {t: tTestTab} = useTranslation('translation', {keyPrefix: 'matchDetailsPage.matchTabs.testResults'});
  const {t: tStatuses} = useTranslation('translation', {keyPrefix: 'talentPoolPage.statuses'});
  const {t: tTable} = useTranslation('translation', {keyPrefix: 'matchesPage.tableColumns'});
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {company} = useContext(CompanyContext);
  const companyId = company.data?.resourceId || '';
  const [createMatchError, setCreateMatchError] = useState<string | null>(null);
  const [isCreateMatchDialogOpen, setIsCreateMatchDialogOpen] = useState(false);
  const [poolMatchNote, setPoolMatchNote] = useState('');
  const [qualificationSectionsOpen, setQualificationSectionsOpen] = useState<Record<QualificationSectionId, boolean>>({
    must: false,
    can: false,
  });
  const [testResultsOpen, setTestResultsOpen] = useState(true);

  const {makeManualRequestWithParams: createTalentPoolMatch, isLoading: isCreatingMatch} = useApiWrite<
    Post.CreateTalentPoolMatch,
    CreateTalentPoolMatchResponse
  >({
    method: 'post',
    disableLoadingOverlay: true,
    onSuccess: (data) => {
      setIsCreateMatchDialogOpen(false);
      setPoolMatchNote('');
      const matchId = data?.talentPoolMatch?.resourceId;
      if (!matchId) {
        setCreateMatchError(t('createMatchErrorFallback'));
        return;
      }
      queryClient.resetQueries({queryKey: ['talent-pool']});
      queryClient.resetQueries({queryKey: [TALENT_POOL_SUMMARY_QUERY_KEY, companyId]});
      const qs = new URLSearchParams();
      qs.set(MATCH_DETAILS_RETURN_TO_PARAM, MATCH_DETAILS_FROM_TALENT_POOL);
      const lang = searchParams.get('lang');
      if (lang) {
        qs.set('lang', lang);
      }
      navigate(`/company/recruiting/match/${matchId}?${qs.toString()}`);
    },
    onError: (err) => {
      setCreateMatchError(err?.error_message ?? t('createMatchErrorFallback'));
    },
  });

  const assessment = application.qualificationTestResult;
  const assessmentQuestions = normalizeAssessmentQuestions(assessment?.questions);
  const hasTestResultsSection = !!assessment && (Boolean(assessmentQuestions?.length) || assessment.score != null);

  const statusKey = application.poolStatus ?? '';
  const statusLabel = TALENT_POOL_STATUS_IDS.has(statusKey)
    ? String(tStatuses(statusKey as 'pool' | 'waiting_cv' | 'match'))
    : statusKey.replace(/_/g, ' ');
  const isMatchedRecord = Boolean(application.match?.resourceId);
  const resolvedMatchStatus = application.match?.status;
  const resolvedRejectionInfo = application.match?.rejectionInfo ?? null;

  const matchHref = (() => {
    if (!application.match?.resourceId) {
      return null;
    }
    const qs = new URLSearchParams();
    qs.set(MATCH_DETAILS_RETURN_TO_PARAM, MATCH_DETAILS_FROM_TALENT_POOL);
    const lang = searchParams.get('lang');
    if (lang) {
      qs.set('lang', lang);
    }
    return `/company/recruiting/match/${application.match.resourceId}?${qs.toString()}`;
  })();

  const analysis = application.jobMatchScoreAnalysis;
  const mustDetails = analysis?.mustCriteria?.details;
  const canList = analysis?.canCriteria;
  const mustRows = Array.isArray(mustDetails)
    ? mustDetails.map(parseCriterionDetail).filter((r): r is JobMatchCriterionDetail => r != null)
    : [];
  const canRows = Array.isArray(canList)
    ? canList.map(parseCriterionDetail).filter((r): r is JobMatchCriterionDetail => r != null)
    : [];
  const mustTotalMet = analysis?.mustCriteria?.totalMet;
  const mustTotalRequired = analysis?.mustCriteria?.totalRequired;
  const showMustProgress = typeof mustTotalMet === 'number' && typeof mustTotalRequired === 'number';
  const hasJobAnalysisText = Boolean(analysis?.summary);
  const hasCriteriaSections = mustRows.length > 0 || canRows.length > 0;
  const hasJobAnalysisContent = hasJobAnalysisText || hasCriteriaSections;
  const jobMatchScoreAnalysisUnavailable = application.jobMatchScoreAnalysis == null;
  const showJobAnalysisSection = hasJobAnalysisContent || jobMatchScoreAnalysisUnavailable;
  const scoreBreakdownItems = [
    {
      key: 'total',
      label: t('scores.total'),
      value: application.totalMatch != null ? `${application.totalMatch}%` : '--%',
    },
    {key: 'job', label: t('scores.job'), value: application.jobMatch != null ? `${application.jobMatch}%` : '--%'},
    {
      key: 'test',
      label: t('scores.test'),
      value: application.testMatch != null ? `${application.testMatch}%` : '--%',
    },
    {
      key: 'culture',
      label: t('scores.culture'),
      value: application.cultureMatch != null ? `${application.cultureMatch}%` : '--%',
    },
  ];

  const canCreateMatch = !application.match?.resourceId && Boolean(application.resourceId) && Boolean(companyId);
  const canConfirmCreateMatch = !isCreatingMatch;
  const hasActionControls = Boolean(canCreateMatch || matchHref || createMatchError);
  const mustProgressLabel = showMustProgress
    ? t('mustCriteriaProgress', {met: mustTotalMet, required: mustTotalRequired})
    : null;

  const toggleQualificationSection = (id: QualificationSectionId) => {
    setQualificationSectionsOpen((prev) => ({...prev, [id]: !prev[id]}));
  };

  const handleOpenCreateMatchDialog = () => {
    setCreateMatchError(null);
    setIsCreateMatchDialogOpen(true);
  };

  const handleCloseCreateMatchDialog = () => {
    if (isCreatingMatch) {
      return;
    }
    setIsCreateMatchDialogOpen(false);
  };

  const handleCreateMatchConfirm = () => {
    setCreateMatchError(null);
    if (!companyId) {
      setCreateMatchError(t('createMatchErrorFallback'));
      return;
    }
    const trimmedPoolMatchNote = poolMatchNote.trim();
    void createTalentPoolMatch({
      method: 'post',
      path: PostRoutes[Post.CreateTalentPoolMatch](companyId),
      body: {
        talentPoolMatch: {
          companyJobPostingApplicationResourceId: application.resourceId,
          ...(trimmedPoolMatchNote ? {companyPoolMatchNote: trimmedPoolMatchNote} : {}),
        },
      },
    });
  };

  return (
    <Box sx={cardShellSx}>
      <Grid container spacing={2} alignItems='flex-start'>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              gap: 2,
              alignItems: {xs: 'stretch', sm: 'flex-start'},
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{flex: 1, minWidth: 0, pr: {sm: 1}}}>
              <Typography sx={{fontSize: '12px', lineHeight: 1.3, color: greenColors[100], mb: 0.5}}>
                {tTable('position')}
              </Typography>
              <Typography variant='h3' component='h3' sx={{color: greenColors[900]}}>
                {application.companyJobPostingTitle || '—'}
              </Typography>
              <Box sx={{display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 0.5, alignItems: 'baseline'}}>
                {application.createdAt ? (
                  <Typography variant='body2' component='span'>
                    <strong>{t('appliedAt')}:</strong> {transformDate(application.createdAt, 'numeric')}
                  </Typography>
                ) : null}
                <Typography variant='body2' component='span'>
                  <strong>{t('status')}:</strong> {statusLabel || '—'}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignSelf: {xs: 'flex-end', sm: 'flex-start'},
                flexShrink: 0,
              }}
            >
              <EmpionMatchScoresGrid
                ariaLabel={`${t('scores.total')}, ${t('scores.job')}, ${t('scores.test')}, ${t('scores.culture')}`}
                items={scoreBreakdownItems}
                variant='default'
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', md: 'row'},
              justifyContent: 'space-between',
              alignItems: {xs: 'stretch', md: 'flex-start'},
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 2,
                rowGap: 0.5,
                alignItems: 'baseline',
              }}
            >
              {isMatchedRecord ? (
                <Typography variant='body2' component='span'>
                  <strong>{t('matchStatus')}:</strong> <MatchStatusLabel status={resolvedMatchStatus} />
                </Typography>
              ) : null}
            </Box>
            {hasActionControls ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: {xs: 'flex-start', md: 'flex-end'},
                  gap: 1,
                }}
              >
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end'}}>
                  {canCreateMatch ? (
                    <EmpionButton
                      type='button'
                      variant='primary'
                      size='small'
                      disabled={isCreatingMatch}
                      onClick={handleOpenCreateMatchDialog}
                    >
                      {t('createMatch')}
                    </EmpionButton>
                  ) : null}
                  {matchHref ? (
                    <EmpionButton
                      component={RouterLink}
                      to={matchHref}
                      variant='primary'
                      size='small'
                      endIcon={<OpenInNewIcon sx={{fontSize: 16}} />}
                    >
                      {t('viewMatch')}
                    </EmpionButton>
                  ) : null}
                </Box>
                {createMatchError ? (
                  <Alert
                    severity='error'
                    sx={{width: {xs: '100%', md: 'auto'}}}
                    onClose={() => setCreateMatchError(null)}
                  >
                    {createMatchError}
                  </Alert>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Grid>

        {isMatchedRecord ? (
          <Grid item xs={12}>
            <RejectionInfoBox status={resolvedMatchStatus} rejectionInfo={resolvedRejectionInfo} />
          </Grid>
        ) : null}

        {showJobAnalysisSection ? (
          <Grid item xs={12}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1}}>
              <Typography variant='h3'>{t('jobAnalysis')}</Typography>
            </Box>
            {hasJobAnalysisContent ? (
              <>
                {analysis?.summary ? <Typography variant='body2'>{analysis.summary}</Typography> : null}

                {hasCriteriaSections ? (
                  <Box sx={{mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5}}>
                    <Typography variant='h3'>{t('qualificationAnalysisTitle')}</Typography>

                    {mustRows.length > 0 ? (
                      <EmpionCollapsibleSection
                        title={t('mustHaves')}
                        collapsible
                        open={qualificationSectionsOpen.must}
                        onToggle={() => toggleQualificationSection('must')}
                        metaText={mustProgressLabel || undefined}
                      >
                        <Stack spacing={0.5}>
                          {mustRows.map((row, i) => (
                            <TalentPoolCriterionAnalysisRow key={`must-${i}`} row={row} />
                          ))}
                        </Stack>
                      </EmpionCollapsibleSection>
                    ) : null}

                    {canRows.length > 0 ? (
                      <EmpionCollapsibleSection
                        title={t('niceToHaves')}
                        collapsible
                        open={qualificationSectionsOpen.can}
                        onToggle={() => toggleQualificationSection('can')}
                      >
                        <Stack spacing={0.5}>
                          {canRows.map((row, i) => (
                            <TalentPoolCriterionAnalysisRow key={`can-${i}`} row={row} />
                          ))}
                        </Stack>
                      </EmpionCollapsibleSection>
                    ) : null}
                  </Box>
                ) : null}
              </>
            ) : jobMatchScoreAnalysisUnavailable ? (
              <>
                <Box
                  sx={{
                    py: 1.5,
                    px: 1.5,
                    borderRadius: '5px',
                    border: `1px dashed ${orangeColors[3]}`,
                    bgcolor: orangeColors[5],
                  }}
                >
                  <Typography variant='body2' sx={{color: greenColors[100]}}>
                    {t('jobAnalysisPending')}
                  </Typography>
                </Box>
              </>
            ) : null}
          </Grid>
        ) : null}

        {hasTestResultsSection ? (
          <Grid item xs={12} sx={{pt: '4px !important'}}>
            <EmpionCollapsibleSection
              title={tTestTab('title')}
              collapsible
              open={testResultsOpen}
              onToggle={() => setTestResultsOpen((prev) => !prev)}
              metaText={assessment?.score != null ? `${assessment.score}%` : undefined}
            >
              {assessmentQuestions?.length ? <AssessmentResults questions={assessmentQuestions} /> : null}
            </EmpionCollapsibleSection>
          </Grid>
        ) : null}

      </Grid>
      <TalentPoolApplicationMatchDialog
        open={isCreateMatchDialogOpen}
        onClose={handleCloseCreateMatchDialog}
        onConfirm={handleCreateMatchConfirm}
        isCreatingMatch={isCreatingMatch}
        canConfirmCreateMatch={canConfirmCreateMatch}
        poolMatchNote={poolMatchNote}
        onPoolMatchNoteChange={setPoolMatchNote}
      />
    </Box>
  );
};
